import ReplayIcon from "@mui/icons-material/Replay";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { Link, Skeleton, Stack, Typography } from "@mui/material";
import { useConfirmDialog } from "contexts/confirmDialog";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useTenant } from "hooks/settings";
import { usePermission } from "hooks/permissions";
import { useState } from "react";
import {
  ActiveHeatOverride,
  generatedApi,
  useCancelHeatOverrideMutation,
} from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import { OverrideDetailsDialog } from "./OverrideDetailsDialog";
import { useOverrideCostDelta } from "./useOverrideCostSummaries";

const reasonText = (
  reason: string | null,
  subreason: string | null
): string => {
  if (reason === null) {
    return "";
  }
  return subreason === null ? reason : `${reason} (${subreason})`;
};

const Separator = () => (
  <Typography variant="body2" sx={{ color: "text.secondary" }}>
    {"·"}
  </Typography>
);

const OverrideCostDelta = ({
  overrideMixId,
  replacedMixId,
}: {
  overrideMixId: number;
  replacedMixId: number;
}) => {
  const { heat_override_mode: heatOverrideMode } = useTenantData();
  const { deltaPerTonne, isPending } = useOverrideCostDelta({
    overrideMixId,
    replacedMixId,
  });
  const units = useUnitsFormatter(false);
  const { format } = useNumberSerialiser({ decimalPlaces: 2 });

  if (heatOverrideMode !== "catalog") {
    return null;
  }

  if (deltaPerTonne === null) {
    return isPending ? (
      <>
        <Separator />
        <Skeleton variant="text" sx={{ width: 64 }} />
      </>
    ) : null;
  }

  const color = (() => {
    switch (true) {
      case deltaPerTonne < 0:
        return "success.main";
      case deltaPerTonne > 0:
        return "error.main";
      default:
        return "text.secondary";
    }
  })();

  return (
    <>
      <Separator />
      <Typography variant="body2" sx={{ color }}>
        {deltaPerTonne >= 0 ? "+" : ""}
        {format(deltaPerTonne)}
        {units("specific_cost")}
      </Typography>
    </>
  );
};

export const OverrideStartBanner = ({
  override,
  heatCount,
  claimedHeatIds,
}: {
  override: ActiveHeatOverride;
  heatCount: number;
  claimedHeatIds: number[];
}) => {
  const { override_id: overrideId, reason, subreason } = override;
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const canCancel = usePermission(["crane_supervisor"]);
  const dispatch = useAppDispatch();
  const confirm = useConfirmDialog();
  const [cancelOverride, { isLoading: isCancelling, isError: cancelFailed }] =
    useCancelHeatOverrideMutation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleCancel = async () => {
    const confirmed = await confirm({
      title: t("cancelOverrideConfirmTitle"),
      message: t("cancelOverrideConfirmMessage"),
    });
    if (!confirmed) {
      return;
    }
    const cancellation = await cancelOverride({ tenantName, overrideId });
    if ("error" in cancellation) {
      return;
    }
    setDetailsOpen(false);
    dispatch(generatedApi.util.invalidateTags(["heat_queue"]));
  };

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", gap: 0.75, minWidth: 0, pl: 1 }}
    >
      <SubdirectoryArrowRightIcon
        fontSize="inherit"
        sx={{ color: "text.secondary" }}
      />
      <Typography variant="body2" noWrap>
        {t("overrideBannerChanged", {
          count: heatCount,
          reasonText: reasonText(reason, subreason),
        })}
      </Typography>
      <OverrideCostDelta
        overrideMixId={override.override_mix_id}
        replacedMixId={override.replaced_mix.id}
      />
      <Separator />
      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={() => {
          setDetailsOpen(true);
        }}
      >
        {t("viewDetails")}
      </Link>
      {detailsOpen && (
        <OverrideDetailsDialog
          override={override}
          claimedHeatIds={claimedHeatIds}
          onClose={() => {
            setDetailsOpen(false);
          }}
          onCancel={canCancel ? handleCancel : null}
          isCancelling={isCancelling}
          cancelFailed={cancelFailed}
        />
      )}
      {canCancel && (
        <>
          <Separator />
          <Link
            component="button"
            type="button"
            variant="body2"
            color="error"
            onClick={() => {
              if (!isCancelling) {
                void handleCancel();
              }
            }}
          >
            {t("cancel")}
          </Link>
        </>
      )}
      {cancelFailed && (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {t("cancelOverrideError")}
        </Typography>
      )}
    </Stack>
  );
};

export const OverrideEndBanner = ({ endHeatId }: { endHeatId: number }) => {
  const { t } = useTenantTranslation();
  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", gap: 0.75, color: "text.secondary", pl: 1 }}
    >
      <ReplayIcon fontSize="inherit" />
      <Typography variant="body2">
        {t("overrideBannerEnds", { heatId: endHeatId })}
      </Typography>
    </Stack>
  );
};
